import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { AuthContext } from "../../../context/AuthContext";
import {
  FiUsers,
  FiUserPlus,
  FiDollarSign,
  FiTag,
  FiCheckSquare,
  FiInbox,
  FiArrowUpRight,
} from "react-icons/fi";

/* ── Stat card colours (matches AdminDashboard) ── */
const PALETTE = {
  blue:    { icon: "bg-blue-50 text-blue-600",       border: "border-t-blue-500",    ring: "group-hover:ring-blue-200"    },
  violet:  { icon: "bg-violet-50 text-violet-600",   border: "border-t-violet-500",  ring: "group-hover:ring-violet-200"  },
  indigo:  { icon: "bg-indigo-50 text-indigo-600",   border: "border-t-indigo-500",  ring: "group-hover:ring-indigo-200"  },
  emerald: { icon: "bg-emerald-50 text-emerald-600", border: "border-t-emerald-500", ring: "group-hover:ring-emerald-200" },
};

const formatCount = (n) => {
  if (n === null || n === undefined) return null;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
};

/* ── Vertical stat card ── */
const StatCard = ({ icon: Icon, label, value, color = "blue", to, loading }) => {
  const c = PALETTE[color] ?? PALETTE.blue;
  const formatted = formatCount(value);
  const card = (
    <div className={`group relative bg-white rounded-xl border border-gray-100 border-t-2 ${c.border}
      shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-5 flex flex-col gap-3`}>
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ring-2 ring-transparent transition ${c.icon} ${c.ring}`}>
          <Icon size={18} />
        </div>
        {to && (
          <FiArrowUpRight
            size={14}
            className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition"
          />
        )}
      </div>
      <div>
        {loading ? (
          <div className="h-7 w-12 bg-gray-100 rounded animate-pulse" />
        ) : (
          <p className="text-2xl font-bold text-gray-800 leading-none">
            {formatted ?? <span className="text-gray-300">0</span>}
          </p>
        )}
        <p className="text-sm text-gray-500 mt-1.5">{label}</p>
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{card}</Link> : card;
};

const TYPE_BADGE = {
  Company:    "bg-blue-100 text-blue-700",
  Individual: "bg-violet-100 text-violet-700",
};

/* ── Section card wrapper ── */
const Card = ({ title, action, children }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h2>
      {action}
    </div>
    <div className="px-6 py-4">{children}</div>
  </div>
);

/* ── Main ── */
const VendorManagerDashboard = () => {
  const { user } = useContext(AuthContext);
  const [counts, setCounts] = useState({ vendors: null, applications: null, contacts: null, prices: null });
  const [recentVendors, setRecentVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const countOf = (res) =>
      res?.data?.meta?.total ?? (Array.isArray(res?.data?.data) ? res.data.data.length : 0);

    const fetchAll = async () => {
      // Resilient: one failing endpoint should not blank out the others.
      const [vendorsRes, contactsRes, pricesRes] = await Promise.allSettled([
        api.get("/vendors"),
        api.get("/vendor/contact-persons"),
        api.get("/vendor-price-list"),
      ]);

      const vendors =
        vendorsRes.status === "fulfilled" ? vendorsRes.value.data?.data ?? [] : [];

      setCounts({
        vendors: vendors.length,
        // Vendor applications has no backend endpoint yet — shows 0 until wired.
        applications: null,
        contacts: contactsRes.status === "fulfilled" ? countOf(contactsRes.value) : null,
        prices: pricesRes.status === "fulfilled" ? countOf(pricesRes.value) : null,
      });

      setRecentVendors(
        [...vendors]
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          .slice(0, 5)
      );
      setLoading(false);
    };

    fetchAll();
  }, []);

  const tableHeaderCls = "text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-2 border-b border-gray-100";
  const skeletonRows = [...Array(3)].map((_, i) => (
    <tr key={i}><td colSpan={4} className="py-2"><div className="h-8 bg-gray-100 rounded-lg animate-pulse" /></td></tr>
  ));

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const displayName = user?.username || user?.email?.split("@")[0] || "Manager";
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  const vendorName = (v) =>
    v.type === "Company"
      ? v.company_name || "—"
      : `${v.primary_users?.first_name || ""} ${v.primary_users?.last_name || ""}`.trim() || "—";

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl px-7 py-6 text-white shadow-md">
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="absolute -right-20 bottom-0 w-64 h-64 rounded-full bg-indigo-400/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <p className="text-xs font-semibold tracking-widest uppercase text-blue-200 mb-1">{today}</p>
          <h1 className="text-2xl font-bold tracking-tight">{greeting}, {displayName}.</h1>
          <p className="text-sm text-blue-200 mt-1">Here's what's happening across your vendors today.</p>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FiUsers}      label="Vendors"       value={counts.vendors}      color="blue"    to="/vendors"             loading={loading} />
        <StatCard icon={FiInbox}      label="Applications"  value={counts.applications} color="emerald" to="/vendor/applications" loading={loading} />
        <StatCard icon={FiUserPlus}   label="Contacts"      value={counts.contacts}     color="violet"  to="/vendor/contacts"     loading={loading} />
        <StatCard icon={FiDollarSign} label="Price Lists"   value={counts.prices}       color="indigo"  to="/vendor/prices"       loading={loading} />
      </div>

      {/* ── Recent vendors ── */}
      <Card
        title="Recent Vendors"
        action={<Link to="/vendors" className="text-sm text-blue-600 hover:underline font-medium">View all</Link>}
      >
        {loading ? (
          <table className="w-full"><tbody>{skeletonRows}</tbody></table>
        ) : recentVendors.length === 0 ? (
          <p className="text-sm text-gray-400">No vendors yet.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className={tableHeaderCls}>Vendor</th>
                <th className={tableHeaderCls}>Type</th>
                <th className={tableHeaderCls}>Country</th>
                <th className={tableHeaderCls}>Added</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {recentVendors.map((v) => (
                <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-2.5 pr-4">
                    <Link to={`/vendors/${v.id}`} className="text-sm font-medium text-gray-700 hover:text-blue-600 block truncate max-w-[200px]">
                      {vendorName(v)}
                    </Link>
                  </td>
                  <td className="py-2.5 pr-4">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_BADGE[v.type] ?? "bg-gray-100 text-gray-600"}`}>
                      {v.type || "—"}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-sm text-gray-500">{v.country || "—"}</td>
                  <td className="py-2.5 text-sm text-gray-500">
                    {v.createdAt ? new Date(v.createdAt).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ── Quick Actions ── */}
      <Card title="Quick Actions">
        <div className="flex flex-wrap gap-2">
          {[
            { to: "/vendors/create-vendor", icon: FiUserPlus,    label: "New Vendor"    },
            { to: "/vendor/applications",   icon: FiInbox,       label: "Applications"  },
            { to: "/vendor/contacts",       icon: FiUsers,       label: "Contacts"      },
            { to: "/vendor/prices",         icon: FiDollarSign,  label: "Prices"        },
            { to: "/vendor/tags",           icon: FiTag,         label: "Tags"          },
            { to: "/todos",                 icon: FiCheckSquare, label: "To Dos"        },
          ].map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600
                bg-gray-50 border border-gray-200 rounded-lg hover:bg-blue-50 hover:text-blue-700
                hover:border-blue-200 transition-colors"
            >
              <Icon size={14} />
              {label}
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default VendorManagerDashboard;
